import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@heidi/runtime/logger';
import { useEffect } from 'react';
import { type AudioDevice } from 'src-tauri/src/bindings/AudioDevice';
import { type HardwareMicDevice, listenHardwareMicConnected } from './api';

/**
 * The HID and audio interfaces of one dictaphone can enumerate seconds apart:
 * a first plug-in on Windows waits on USB audio driver setup. Each refresh is
 * a full device walk, so back off instead of polling at a fixed rate.
 */
const MATCH_RETRY_DELAYS_MS = [1_000, 2_000, 4_000];

const hex4 = (n: number) => n.toString(16).padStart(4, '0');

/** Matches Rust `AudioDevice.usb_id`. */
const dictaphoneUsbId = ({ vid, pid }: Pick<HardwareMicDevice, 'vid' | 'pid'>) =>
  `${hex4(vid)}:${hex4(pid)}`;

/**
 * Switch the microphone to a dictaphone's own audio input when it is plugged
 * in. Reader-session snapshots (boot, flag on, resume) are not plug-ins, so a
 * dictaphone left connected never overrides the user's later mic choice.
 */
export const useAutoSelectDictaphoneMic = ({
  enabled,
  refreshInputDevices,
  selectInputDevice,
}: {
  enabled: boolean;
  /** Re-enumerate inputs and return the fresh list. */
  refreshInputDevices: () => Promise<AudioDevice[] | undefined>;
  selectInputDevice: (device: AudioDevice) => void;
}): void => {
  useEffect(() => {
    if (!isTauri() || !enabled) return;

    let cancelled = false;
    let unlisten: (() => void) | undefined;
    // The most recent plug-in wins; an older retry loop stops at its next check.
    let latestMatch = 0;

    const selectMatchingInput = async (dictaphone: HardwareMicDevice) => {
      const match = ++latestMatch;
      const isStale = () => cancelled || match !== latestMatch;
      const usbId = dictaphoneUsbId(dictaphone);
      for (let attempt = 0; attempt <= MATCH_RETRY_DELAYS_MS.length; attempt++) {
        if (attempt > 0) {
          await new Promise((r) => setTimeout(r, MATCH_RETRY_DELAYS_MS[attempt - 1]));
        }
        if (isStale()) return;
        const input = (await refreshInputDevices())?.find((d) => d.usb_id === usbId);
        if (isStale()) return;
        if (input) {
          logger.info({
            event_type: 'DICTATION_DICTAPHONE_INPUT_SELECTED',
            details: { model: dictaphone.model, usbId, attempt },
          });
          selectInputDevice(input);
          return;
        }
      }
      logger.warn({
        event_type: 'DICTATION_DICTAPHONE_INPUT_NOT_FOUND',
        details: { model: dictaphone.model, usbId },
      });
    };

    void listenHardwareMicConnected(({ payload }) => {
      if (payload.hotPlug) void selectMatchingInput(payload);
    })
      .then((un) => {
        if (cancelled) un();
        else unlisten = un;
      })
      .catch((err: unknown) => {
        logger.error({
          event_type: 'DICTATION_DICTAPHONE_AUTO_SELECT_LISTEN_FAILED',
          details: { error: sanitizeError(err) },
        });
      });

    return () => {
      cancelled = true;
      unlisten?.();
    };
  }, [enabled, refreshInputDevices, selectInputDevice]);
};
